import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { UserSettings } from '../Types';
import toast from 'react-hot-toast';
import { Model, Prompt } from '../Types';
import { promptService } from '../Services/promptService';
import usePromptState from './state/usePromptState';
import useModal from '../components/Modal/useModal';
import { getBreakpoint } from '../utils/breakpoint';

const useUserAssistans = () => {
    const navigate = useNavigate();
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);
    const dispatch = useAppDispatch();
    const { prompts, deletePrompt, setSelectedPrompt, updatePrompt, getPromptValueById } = usePromptState();
    const modal = useModal();

    const [filter, setFilter] = useState<string>('');
    const [filteredAssistants, setFilteredAssistants] = useState([] as Prompt[]);
    const [assistantsLoaded, setAssistantsLoaded] = useState<boolean>(false);
    const [models, setModels] = useState<Model[]>([]);

    const handleFilterChange = useCallback((value: string) => {
        setFilter(value);
    }, []);

    useEffect(() => {
        if (userSettings.isLoggedIn) {
            promptService.getModels(true).then((response: any) => {
                setModels(response.data);
            });
        }
    }, [userSettings.isLoggedIn]);

    const handleProposeGlobal = useCallback(
        (identifier: string) => {
            if (userSettings.isLoggedIn) {
                promptService.proposeGlobal(identifier).then(() => {
                    updatePrompt(identifier, { proposedGlobal: true });
                    toast.success(i18n.translate({ id: 'toast.proposeGlobalSuccess', sourceName: 'Assistants' }));
                });
            }
        },
        [userSettings.isLoggedIn, i18n, updatePrompt],
    );

    const handleRemoveProposedGlobal = useCallback(
        (identifier: string) => {
            if (userSettings.isLoggedIn) {
                promptService.unproposeGlobal(identifier).then(() => {
                    updatePrompt(identifier, { proposedGlobal: false });
                    toast.success(i18n.translate({ id: 'toast.unproposeGlobalSuccess', sourceName: 'Assistants' }));
                });
            }
        },
        [userSettings.isLoggedIn, i18n, updatePrompt],
    );

    const handleDelete = useCallback(
        async (promptIdentifier: string) => {
            modal.deleteConfirm({
                title: i18n.translate({ id: 'promptDelete.title', sourceName: 'Assistants' }),
                content: i18n.translate({ id: 'promptDelete.text', sourceName: 'Assistants' }),
                onConfirm: () => {
                    if (userSettings.isLoggedIn) {
                        promptService.deletePrompt(promptIdentifier).then(() => {
                            deletePrompt(promptIdentifier);
                        });
                    }
                },
            });
        },
        [modal, userSettings.isLoggedIn, i18n, deletePrompt],
    );

    const createNewChat = useCallback(
        async (promptIdentifier: string) => {
            setSelectedPrompt(getPromptValueById(promptIdentifier));
            navigate(`/start/${promptIdentifier}`);
        },
        [navigate, setSelectedPrompt, getPromptValueById],
    );

    useEffect(() => {
        if (filter) {
            let filtered = prompts.filter((prompt: Prompt) => {
                return (
                    prompt.name.toLowerCase().includes(filter.toLowerCase()) ||
                    prompt.description.toLowerCase().includes(filter.toLowerCase())
                );
            });
            setFilteredAssistants(filtered);
        } else {
            setFilteredAssistants(prompts);
        }
    }, [filter, prompts]);

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;

        promptService
            .getAllUserPromptsPaginated(30)
            .then((response) => {
                if (response && Array.isArray(response)) {
                    setAssistantsLoaded(true);
                } else {
                    setAssistantsLoaded(true);
                }
            })
            .catch((error) => {
                setAssistantsLoaded(true);
            });
    }, [userSettings.isLoggedIn, dispatch, demo]);

    const UserAssistansActions = useMemo(
        () => ({
            createNewChat,
            handleRemoveProposedGlobal,
            handleProposeGlobal,
            handleFilterChange,
            handleDelete,
        }),
        [createNewChat, handleRemoveProposedGlobal, handleProposeGlobal, handleFilterChange, handleDelete],
    );

    const handleNavigate = useCallback(
        (path: string) => {
            navigate(path);
        },
        [navigate],
    );

    return {
        UserAssistansActions,
        userSettings,
        demo,
        i18n,
        getBreakpoint,
        windowWidth,
        handleNavigate,
        prompts,
        filteredAssistants,
        filter,
        assistantsLoaded,
        models,
    };
};

export default useUserAssistans;
